import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function humanCountdown(iso) {
  const end = new Date(iso).getTime();
  const now = Date.now();
  const ms = end - now;
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

export default function LiveSpecialsFeed({ barId = null }) {
  const [specials, setSpecials] = useState([]);
  const [, setTick] = useState(0);

  const load = async () => {
    try {
      const url = `${API}/specials/active${barId ? `?bar_id=${barId}` : ''}`;
      const res = await axios.get(url);
      setSpecials(res.data?.specials || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => { setTick((t) => t + 1); load(); }, 60000);
    return () => clearInterval(iv);
  }, [barId]);

  if (!specials.length) return null;

  return (
    <div data-testid="live-specials-feed" className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="h-4 w-4 text-yellow-300" />
        <p className="text-xs font-mono text-yellow-300 tracking-widest">◆ LIVE BAR SPECIALS</p>
      </div>
      {specials.map((s) => {
        const isActive = s.status === 'active';
        const cd = isActive ? humanCountdown(s.active_end_at) : null;
        return (
          <Card
            key={s.id}
            data-testid={`live-special-${s.id}`}
            className={`p-3 border ${isActive ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/40' : 'bg-zinc-800/60 border-zinc-600'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-white text-sm">{s.title}</p>
                  <Badge className={isActive ? 'bg-yellow-500 text-black' : 'bg-zinc-600'}>
                    {isActive ? 'LIVE' : 'ENDED'}
                  </Badge>
                </div>
                {s.description && <p className="text-xs text-zinc-300">{s.description}</p>}
                <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {isActive ? cd : `Ended`} · {s.owner_name || 'Bar owner'}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
